// src/components/routing/ZoneManagementComponent.js
// FlowSense AI — Enhanced Zone Management v2.0
// Glass Morphism, Framer Motion, Enhanced Map, Performance Optimized
//
// --- UPDATE (Zones / Constituencies v2.1) -------------------------------------------------
//   • A zone and a constituency are the same thing: each zone can carry its constituency name
//     (shown as "Zone · Constituency"), set when drawing or later with Edit.
//   • Every zone has its OWN colour — stored per zone (pick from 24 distinct colours or any hex)
//     or assigned automatically with no repeats; the same colour is used on Field Operations.
//     (Before, colours were by row position and repeated after 8 zones.)
//   • Edit a zone: name, constituency, description, colour (PATCH /field-ops/zones-intel/zones/:id).
//   • Live per-zone numbers from FlowSense: open / critical jobs, field staff online, risk —
//     in the table and the map pop-ups; refreshed by the WebSocket (field_ops_zone_update).
//   • "Map Coverage" now shows the real share of located open jobs inside a zone ("—" until
//     known) instead of a fixed 100 %.
//   Original create / delete / draw logic is unchanged.
// --- END UPDATE ---------------------------------------------------------------------------

import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import {
    Box, Button, Table, Thead, Tbody, Tr, Th, Td, TableContainer, Spinner, Alert,
    AlertIcon, HStack, useToast, Heading, Flex, IconButton, useDisclosure,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    FormControl, FormLabel, Input, Textarea, VStack, Text, Badge, Card, CardBody,
    useColorModeValue, InputGroup, InputLeftElement, InputRightElement, Tooltip,
    SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText, AlertDialog, AlertDialogBody,
    AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay,
    Icon, Skeleton, Progress, Tag, TagLabel, TagLeftIcon, Collapse,
} from '@chakra-ui/react';
import {
    DeleteIcon, SearchIcon, InfoIcon, CheckIcon, WarningIcon, CloseIcon,
} from '@chakra-ui/icons';
import {
    MdMap, MdLayers, MdCheckCircle, MdWarning, MdRefresh, MdLocationOn,
    MdDelete, MdDraw, MdGridView, MdTimeline,
} from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, GeoJSON, FeatureGroup } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import L from 'leaflet';

import * as assignmentZoneService from '../../services/assignmentZoneService';
// --- UPDATE
import { MdEdit, MdPalette } from 'react-icons/md';
import fieldOpsZoneApi from '../../services/fieldOpsZoneApi';
import webSocketService from '../../services/websocketService';
import { ZONE_PALETTE, assignZoneColors, isHexColor, RISK_LEVELS } from './zoneGeo';
// --- END UPDATE

// Fix Leaflet default icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// ─────────────────────────────────────────────────────────────────────────────
// MOTION WRAPPERS & PRESETS
// ─────────────────────────────────────────────────────────────────────────────
const MotionBox  = motion(Box);
const MotionFlex = motion(Flex);
const MotionTr   = motion(Tr);

const ANIM = {
    fadeInUp:   { initial:{ opacity:0, y:16 },  animate:{ opacity:1, y:0 } },
    fadeInDown: { initial:{ opacity:0, y:-16 }, animate:{ opacity:1, y:0 } },
    scaleIn:    { initial:{ opacity:0, scale:0.92 }, animate:{ opacity:1, scale:1 } },
    stagger:    (i, base=0.05) => ({ delay: i * base }),
    spring:     { type:'spring', stiffness:300, damping:24 },
    smooth:     { duration:0.3, ease:[0.25, 0.46, 0.45, 0.94] },
};

const PulseIndicator = ({ color='green', size=2 }) => (
    <Box position="relative" display="inline-flex" w={`${size*4}px`} h={`${size*4}px`} flexShrink={0}>
        <MotionBox position="absolute" inset={0} borderRadius="full" bg={`${color}.400`}
            animate={{ scale:[1,1.8,1], opacity:[0.7,0,0.7] }}
            transition={{ duration:2, repeat:Infinity, ease:'easeInOut' }} />
        <Box position="absolute" inset={0} borderRadius="full" bg={`${color}.500`} />
    </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// SHARED SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
const EnhancedStatCard = memo(({ label, value, helpText, icon, color='blue', isLoading:loading, children }) => {
    const cardBg      = useColorModeValue('white','gray.800');
    const borderColor = useColorModeValue('gray.200','gray.600');
    const mutedColor  = useColorModeValue('gray.500','gray.400');
    return (
        <MotionBox whileHover={{ y:-3, boxShadow:'0 8px 25px rgba(0,0,0,0.1)' }} transition={{ duration:0.2 }}>
            <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl"
                overflow="hidden" position="relative">
                <Box position="absolute" top={0} left={0} right={0} h="3px"
                    bgGradient={`linear(to-r, ${color}.400, ${color}.600)`} />
                <CardBody py={4} px={5}>
                    {loading ? (
                        <VStack align="start" spacing={2}>
                            <Skeleton h="12px" w="80px" borderRadius="md" />
                            <Skeleton h="28px" w="50px" borderRadius="md" />
                        </VStack>
                    ) : (
                        <HStack justify="space-between" align="start">
                            <Stat size="sm">
                                <StatLabel fontSize="xs" fontWeight="600" color={mutedColor}
                                    textTransform="uppercase" letterSpacing="wider">{label}</StatLabel>
                                <StatNumber fontSize="2xl" fontWeight="800" color={`${color}.500`}>
                                    {children || value}
                                </StatNumber>
                                <StatHelpText fontSize="xs" mb={0}>{helpText}</StatHelpText>
                            </Stat>
                            {icon && (
                                <Box p={2} borderRadius="xl" bg={`${color}.50`}>
                                    <Icon as={icon} boxSize={5} color={`${color}.500`} />
                                </Box>
                            )}
                        </HStack>
                    )}
                </CardBody>
            </Card>
        </MotionBox>
    );
});

const EmptyState = memo(({ icon:IconComp, title, subtitle }) => (
    <Flex direction="column" align="center" justify="center" py={10} gap={3}>
        <MotionBox animate={{ y:[0,-6,0] }} transition={{ duration:3, repeat:Infinity, ease:'easeInOut' }}>
            <Icon as={IconComp || MdMap} boxSize={14} color="gray.300" />
        </MotionBox>
        <Text fontWeight="700" color="gray.400">{title}</Text>
        {subtitle && <Text fontSize="sm" color="gray.400" textAlign="center" maxW="300px">{subtitle}</Text>}
    </Flex>
));

// ─────────────────────────────────────────────────────────────────────────────
// UTILITIES (original logic preserved)
// ─────────────────────────────────────────────────────────────────────────────
const parseGeoJSON = (jsonString) => {
    if (!jsonString) return null;
    try {
        return typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
    } catch (e) {
        console.error("Invalid GeoJSON string:", jsonString, e);
        return null;
    }
};

const ZONE_COLORS = ['#3182ce', '#38a169', '#dd6b20', '#d53f8c', '#805ad5', '#319795', '#d69e2e', '#e53e3e'];
const getZoneColor = (index) => ZONE_COLORS[index % ZONE_COLORS.length];

// --- UPDATE: colour picker (24 distinct colours, used ones marked, or any hex) ---
const ColorPicker = memo(({ value, onChange, usedColors = [] }) => {
    const used = new Set(usedColors.map((c) => String(c).toLowerCase()));
    return (
        <Box>
            <Flex wrap="wrap" gap={1.5} mb={2}>
                {ZONE_PALETTE.map((c) => {
                    const on = String(value || '').toLowerCase() === c;
                    return (
                        <Tooltip key={c} label={used.has(c) && !on ? `${c} — used by another zone` : c} hasArrow>
                            <Box as="button" type="button" aria-label={`Colour ${c}`} aria-pressed={on} onClick={() => onChange(c)}
                                w="24px" h="24px" borderRadius="md" bg={c} borderWidth={on ? '3px' : '1px'} borderColor={on ? 'gray.800' : 'whiteAlpha.700'}
                                opacity={used.has(c) && !on ? 0.35 : 1} _hover={{ transform: 'scale(1.12)' }} transition="all 0.12s" />
                        </Tooltip>
                    );
                })}
            </Flex>
            <HStack>
                <Input size="sm" type="color" w="56px" p={0.5} value={isHexColor(value) ? value : '#2563eb'} onChange={(e) => onChange(e.target.value)} aria-label="Custom colour" />
                <Input size="sm" value={value || ''} placeholder="#2563eb or empty = automatic" onChange={(e) => onChange(e.target.value)} maxW="200px" fontFamily="mono" aria-label="Colour hex" />
                {value && <Button size="xs" variant="ghost" onClick={() => onChange('')}>Automatic</Button>}
            </HStack>
        </Box>
    );
});
// --- END UPDATE ---

// ─────────────────────────────────────────────────────────────────────────────
// ZONE ROW — memoized table row
// ─────────────────────────────────────────────────────────────────────────────
const ZoneRow = memo(({ zone, index, onDelete, color: zoneColor, intel, onEdit }) => { // --- UPDATE: + color, intel, onEdit
    const mutedColor = useColorModeValue('gray.500','gray.400');
    const hoverBg    = useColorModeValue('gray.50','gray.700');
    const color = zoneColor || getZoneColor(index); // --- UPDATE: the zone's own colour
    const rl = intel ? (RISK_LEVELS[intel.risk?.level] || RISK_LEVELS.low) : null; // --- UPDATE
    return (
        <MotionTr initial={{ opacity:0, x:-10 }} animate={{ opacity:1, x:0 }}
            exit={{ opacity:0, x:20 }}
            transition={{ delay: index * 0.03, duration:0.3 }}
            _hover={{ bg:hoverBg }}
            style={{ transition:'background 0.15s' }}>
            <Td>
                <HStack spacing={2}>
                    <Box w="14px" h="14px" borderRadius="md" bg={color}
                        boxShadow={`0 0 8px ${color}40`} flexShrink={0} />
                    {/* --- UPDATE: constituency under the name */}
                    <Box>
                        <Text fontWeight="700" fontSize="sm">{zone.name}</Text>
                        {(intel?.constituency || zone.constituency) && (intel?.constituency || zone.constituency) !== zone.name && (
                            <Text fontSize="2xs" color={mutedColor}>Constituency: {intel?.constituency || zone.constituency}</Text>
                        )}
                    </Box>
                </HStack>
            </Td>
            <Td>
                <Text noOfLines={2} maxW="300px" fontSize="sm">
                    {zone.description || (
                        <Text as="span" color="gray.400" fontStyle="italic">No description</Text>
                    )}
                </Text>
            </Td>
            {/* --- UPDATE: live numbers */}
            <Td isNumeric><Text fontWeight="700" fontSize="sm">{intel ? intel.stats.open : '—'}</Text>{intel?.stats.critical ? <Text fontSize="2xs" color="red.500">{intel.stats.critical} critical</Text> : null}</Td>
            <Td isNumeric><Text fontSize="sm" color={intel && intel.stats.open && !intel.stats.techs_online ? 'red.500' : undefined}>{intel ? `${intel.stats.techs_online}/${intel.stats.techs_present}` : '—'}</Text></Td>
            <Td>{intel ? <Badge colorScheme={rl.scheme} borderRadius="full" fontSize="2xs">{intel.risk.score} · {rl.label}</Badge> : <Text fontSize="xs" color={mutedColor}>—</Text>}</Td>
            {/* --- END UPDATE */}
            <Td>
                <Text fontSize="xs" color={mutedColor}>
                    {new Date(zone.created_at).toLocaleDateString()}
                </Text>
            </Td>
            <Td>
                {/* --- UPDATE: edit */}
                {onEdit && (
                    <Tooltip label="Edit zone · constituency · colour" hasArrow>
                        <IconButton icon={<Icon as={MdEdit} />} size="xs" variant="ghost" mr={1}
                            onClick={() => onEdit(zone)} aria-label={`Edit ${zone.name}`} borderRadius="full" />
                    </Tooltip>
                )}
                <Tooltip label="Delete Zone" hasArrow>
                    <IconButton icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost"
                        onClick={() => onDelete(zone)} aria-label="Delete Zone" borderRadius="full"
                        _hover={{ bg:'red.50' }} />
                </Tooltip>
            </Td>
        </MotionTr>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const ZoneManagementComponent = () => {
    // ── State (all original) ─────────────────────────────────────────────────
    const [zones, setZones] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [zoneToDelete, setZoneToDelete] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const toast = useToast();
    const cancelRef = useRef();

    const [zoneName, setZoneName] = useState('');
    const [zoneDescription, setZoneDescription] = useState('');
    const [tempGeoJSON, setTempGeoJSON] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [drawnItems, setDrawnItems] = useState(new L.FeatureGroup());
    const mapRef = useRef(null);
    // --- UPDATE
    const [zoneConstituency, setZoneConstituency] = useState('');
    const [zoneColor, setZoneColor] = useState('');
    const [intel, setIntel] = useState(null);
    const [editing, setEditing] = useState(null);
    const [editForm, setEditForm] = useState({ name:'', constituency:'', description:'', color:'' });
    const [editErrors, setEditErrors] = useState({});
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
    const intelTimer = useRef(null);
    const editInitial = useRef(null);
    const mountedRef = useRef(true);
    // --- END UPDATE

    // ── Theme tokens ─────────────────────────────────────────────────────────
    const bgColor        = useColorModeValue('white', 'gray.800');
    const borderColor    = useColorModeValue('gray.200', 'gray.600');
    const mutedColor     = useColorModeValue('gray.500', 'gray.400');
    const searchBg       = useColorModeValue('white', 'gray.700');
    const headerGradient = useColorModeValue(
        'linear(to-r, teal.500, blue.500)',
        'linear(to-r, teal.400, blue.400)'
    );
    const infoBg         = useColorModeValue('blue.50', 'blue.900');

    // ── Data Fetching (original logic) ───────────────────────────────────────
    const fetchZones = useCallback(async () => {
        setIsLoading(true); setError(null);
        try {
            const data = await assignmentZoneService.fetchAssignmentZones();
            setZones(Array.isArray(data) ? data : []);
        } catch (err) {
            const errorMessage = err?.response?.data?.message || err?.message || 'Failed to fetch assignment zones.';
            setError(errorMessage);
            toast({ title:'Error fetching zones', description:errorMessage, status:'error', duration:5000, isClosable:true });
        } finally { setIsLoading(false); }
    }, [toast]);

    useEffect(() => { fetchZones(); }, [fetchZones]);

    // --- UPDATE: live per-zone numbers + colours from FlowSense (optional — page works without) ---
    const fetchIntel = useCallback(async () => {
        try { const o = await fieldOpsZoneApi.overview(); if (mountedRef.current) setIntel(o); }
        catch (_) { /* zone intelligence not deployed / no access → table shows "—" */ }
    }, []);
    useEffect(() => {
        mountedRef.current = true;
        fetchIntel();
        const bump = () => { clearTimeout(intelTimer.current); intelTimer.current = setTimeout(fetchIntel, 1500); };
        webSocketService.subscribe('field_ops_zone_update', bump);
        return () => { mountedRef.current = false; clearTimeout(intelTimer.current); webSocketService.unsubscribe('field_ops_zone_update', bump); };
    }, [fetchIntel]);
    const intelById = useMemo(() => new Map((intel?.zones || []).map((z) => [String(z.id), z])), [intel]);
    const colorMap = useMemo(() => {
        const auto = assignZoneColors(zones.map((z) => ({ id: z.id, color: intelById.get(String(z.id))?.color || z.color })));
        return auto;
    }, [zones, intelById]);
    const nextFreeColor = useMemo(() => {
        const used = new Set([...colorMap.values()].map((c) => String(c).toLowerCase()));
        return ZONE_PALETTE.find((c) => !used.has(c)) || ZONE_PALETTE[zones.length % ZONE_PALETTE.length];
    }, [colorMap, zones.length]);
    const coveragePct = useMemo(() => {
        const located = intel?.totals?.located_work_orders;
        if (!located) return null;
        return Math.round(((located - (intel.unzoned?.work_orders || 0)) / located) * 100);
    }, [intel]);
    // --- END UPDATE ---

    // ── Derived data ─────────────────────────────────────────────────────────
    const zoneStats = useMemo(() => ({
        total: zones.length,
        avgArea: zones.length > 0 ? 'Calculated' : 'N/A',
        coverage: zones.length > 0 ? '100%' : '0%',
    }), [zones]);

    const filteredZones = useMemo(() => {
        if (!searchTerm) return zones;
        return zones.filter(zone =>
            zone.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            zone.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [zones, searchTerm]);

    // ── Handlers (all original logic preserved) ──────────────────────────────
    const validateForm = useCallback(() => {
        const errors = {};
        if (!zoneName || !zoneName.trim()) errors.name = 'Zone name is required';
        else if (zoneName.length < 3) errors.name = 'Zone name must be at least 3 characters';
        else if (zoneName.length > 255) errors.name = 'Zone name must not exceed 255 characters';
        if (zoneDescription && zoneDescription.length > 500) errors.description = 'Description must not exceed 500 characters';
        if (!tempGeoJSON) errors.polygon = 'Please draw a zone on the map';
        const duplicate = zones.find(z => z.name?.toLowerCase() === zoneName.trim().toLowerCase());
        if (duplicate) errors.name = 'A zone with this name already exists';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }, [zoneName, zoneDescription, tempGeoJSON, zones]);

    const handleOnCreated = useCallback((e) => {
        const layer = e.layer;
        drawnItems.clearLayers();
        drawnItems.addLayer(layer);
        const geoJson = layer.toGeoJSON();
        setTempGeoJSON(JSON.stringify(geoJson.geometry));
        setFormErrors(prev => ({ ...prev, polygon: null }));
        onOpen();
    }, [drawnItems, onOpen]);

    const handleOnEdited = useCallback((e) => {
        e.layers.eachLayer((layer) => {
            const geoJson = layer.toGeoJSON();
            setTempGeoJSON(JSON.stringify(geoJson.geometry));
        });
        toast({ title:'Zone updated', description:'Zone boundaries have been modified', status:'info', duration:2000, isClosable:true });
    }, [toast]);

    const handleOnDeleted = useCallback(() => {
        setTempGeoJSON(null);
        toast({ title:'Zone removed', description:'The drawn zone has been removed from the map', status:'info', duration:2000, isClosable:true });
    }, [toast]);

    const handleDeleteZone = useCallback(async () => {
        if (!zoneToDelete) return;
        setIsLoading(true);
        try {
            await assignmentZoneService.deleteAssignmentZone(zoneToDelete.id);
            toast({ title:'Zone deleted successfully', description:`"${zoneToDelete.name}" has been removed`, status:'success', duration:3000, isClosable:true });
            fetchZones(); onDeleteClose(); fetchIntel(); // --- UPDATE: + fetchIntel
        } catch (err) {
            const errorMessage = err?.response?.data?.message || err?.message || 'Failed to delete zone';
            toast({ title:'Error deleting zone', description:errorMessage, status:'error', duration:5000, isClosable:true });
        } finally { setIsLoading(false); }
    }, [zoneToDelete, fetchZones, onDeleteClose, toast, fetchIntel]); // --- UPDATE: deps

    const handleSaveZone = useCallback(async () => {
        if (!validateForm()) {
            toast({ title:'Validation Error', description:'Please fix the errors in the form', status:'error', duration:3000, isClosable:true });
            return;
        }
        setIsLoading(true);
        try {
            const created = await assignmentZoneService.createAssignmentZone({ name: zoneName.trim(), description: zoneDescription.trim(), polygonGeoJSON: tempGeoJSON });
            // --- UPDATE: constituency + colour are saved on the new zone
            try {
                let newId = created?.id ?? created?.data?.id ?? created?.zone?.id;
                if (newId === undefined) {
                    const list = await assignmentZoneService.fetchAssignmentZones();
                    newId = (Array.isArray(list) ? list : []).find((z) => z.name?.toLowerCase() === zoneName.trim().toLowerCase())?.id;
                }
                const extra = {};
                if (zoneConstituency.trim()) extra.constituency = zoneConstituency.trim();
                if (isHexColor(zoneColor)) extra.color = zoneColor;
                if (newId !== undefined && Object.keys(extra).length) await fieldOpsZoneApi.updateZone(newId, extra);
            } catch (e) {
                toast({ title:'Zone saved — colour / constituency not saved', description:e?.message, status:'warning', duration:5000, isClosable:true });
            }
            // --- END UPDATE
            toast({ title:'Zone created successfully', description:`"${zoneName}" has been added`, status:'success', duration:3000, isClosable:true });
            fetchZones(); resetForm(); fetchIntel(); // --- UPDATE: + fetchIntel
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save zone';
            toast({ title:'Error saving zone', description:errorMessage, status:'error', duration:5000, isClosable:true });
        } finally { setIsLoading(false); }
    }, [validateForm, zoneName, zoneDescription, tempGeoJSON, fetchZones, toast, zoneConstituency, zoneColor, fetchIntel]); // --- UPDATE: deps

    const resetForm = useCallback(() => {
        setZoneName(''); setZoneDescription(''); setTempGeoJSON(null); setFormErrors({});
        setZoneConstituency(''); setZoneColor(''); // --- UPDATE
        drawnItems.clearLayers(); onClose();
    }, [drawnItems, onClose]);

    const handleClearDrawing = useCallback(() => {
        drawnItems.clearLayers(); setTempGeoJSON(null);
        toast({ title:'Drawing cleared', status:'info', duration:2000, isClosable:true });
    }, [drawnItems, toast]);

    // --- UPDATE: edit zone · constituency · colour ---
    const openEdit = useCallback((zone) => {
        const i = intelById.get(String(zone.id));
        setEditing(zone);
        const initial = { name: zone.name || '', constituency: i?.constituency || zone.constituency || '', description: zone.description || '',
            color: i?.color_source === 'stored' ? i.color : (isHexColor(zone.color) ? zone.color : '') };
        setEditForm(initial);
        editInitial.current = initial;
        setEditErrors({});
        onEditOpen();
    }, [intelById, onEditOpen]);
    const saveEdit = useCallback(async () => {
        const errs = {};
        const name = editForm.name.trim();
        if (name.length < 3) errs.name = 'Zone name must be at least 3 characters';
        else if (zones.some((z) => z.id !== editing?.id && z.name?.toLowerCase() === name.toLowerCase())) errs.name = 'A zone with this name already exists';
        if (editForm.color && !isHexColor(editForm.color)) errs.color = 'Use a hex colour like #2563eb (or leave empty for automatic)';
        setEditErrors(errs);
        if (Object.keys(errs).length) return;
        setIsSavingEdit(true);
        try {
            // only fields that changed are sent (a colour stored earlier is never wiped by accident)
            const init = editInitial.current || {};
            const body = {};
            if (name !== init.name) body.name = name;
            if (editForm.constituency.trim() !== (init.constituency || '')) body.constituency = editForm.constituency.trim() || null;
            if (editForm.description.trim() !== (init.description || '')) body.description = editForm.description.trim() || null;
            if ((editForm.color || '') !== (init.color || '')) body.color = editForm.color || null;
            if (!Object.keys(body).length) { onEditClose(); return; }
            await fieldOpsZoneApi.updateZone(editing.id, body);
            toast({ title:'Zone updated', description:`"${name}" saved`, status:'success', duration:3000, isClosable:true });
            onEditClose(); fetchZones(); fetchIntel();
        } catch (e) {
            if (e.status === 409) setEditErrors({ name: e.message });
            toast({ title:'Could not update zone', description: e.status === 404 ? 'Zone editing needs the FlowSense zone service (routes/fieldOpsZoneRoutes.js).' : e.message, status:'error', duration:5000, isClosable:true });
        } finally { setIsSavingEdit(false); }
    }, [editForm, editing, zones, toast, onEditClose, fetchZones, fetchIntel]);
    // --- END UPDATE ---

    const initiateDelete = useCallback((zone) => {
        setZoneToDelete(zone); onDeleteOpen();
    }, [onDeleteOpen]);

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <Box>
            {/* ═══════ HEADER ═══════ */}
            <MotionFlex {...ANIM.fadeInDown} transition={ANIM.smooth}
                justifyContent="space-between" alignItems="center" mb={6}>
                <HStack spacing={4}>
                    <MotionBox initial={{ scale:0, rotate:-180 }} animate={{ scale:1, rotate:0 }}
                        transition={{ type:'spring', stiffness:200, damping:15 }}>
                        <Box bgGradient={headerGradient} p={3} borderRadius="xl"
                            boxShadow="0 4px 14px rgba(49,130,206,0.3)">
                            <Icon as={MdMap} color="white" boxSize={6} />
                        </Box>
                    </MotionBox>
                    <Box>
                        <Heading size="md" fontWeight="800">Zone / Constituency Management</Heading>
                        <HStack spacing={2} mt={0.5}>
                            <Text color={mutedColor} fontSize="sm">Define geographic zones for automated work order assignment</Text>
                            <PulseIndicator color="teal" size={1.5} />
                        </HStack>
                    </Box>
                </HStack>
                <Tooltip label="Refresh" hasArrow>
                    <IconButton icon={<Icon as={MdRefresh} />} variant="ghost" borderRadius="full"
                        onClick={fetchZones} isLoading={isLoading} aria-label="Refresh"
                        _hover={{ transform:'rotate(180deg)' }} transition="all 0.3s" />
                </Tooltip>
            </MotionFlex>

            {/* ═══════ STAT CARDS ═══════ */}
            <SimpleGrid columns={{ base:1, md:3 }} spacing={4} mb={6}>
                <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(0), ...ANIM.smooth }}>
                    <EnhancedStatCard label="Total Zones" value={zoneStats.total}
                        helpText="Defined areas" icon={MdLayers} color="blue"
                        isLoading={isLoading && zones.length === 0} />
                </MotionBox>
                <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(1), ...ANIM.smooth }}>
                    <EnhancedStatCard label="Map Coverage" value={intel ? (coveragePct === null ? '—' : `${coveragePct}%`) : '—'}
                        helpText={intel ? `Open jobs inside a zone · ${intel.unzoned?.work_orders ?? 0} outside` : 'Open jobs inside a zone'} icon={MdGridView} color="teal"
                        isLoading={isLoading && zones.length === 0} />
                </MotionBox>
                <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(2), ...ANIM.smooth }}>
                    <EnhancedStatCard label="Status" helpText="System ready"
                        icon={zones.length > 0 ? MdCheckCircle : MdWarning}
                        color={zones.length > 0 ? 'green' : 'orange'}
                        isLoading={isLoading && zones.length === 0}>
                        <Badge colorScheme={zones.length > 0 ? 'green' : 'orange'} borderRadius="full"
                            fontSize="xs" px={3} py={1}>
                            {zones.length > 0 ? 'Active' : 'Setup Required'}
                        </Badge>
                    </EnhancedStatCard>
                </MotionBox>
            </SimpleGrid>

            {/* ═══════ INSTRUCTIONS CARD ═══════ */}
            <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(3), ...ANIM.smooth }}>
                <Card mb={6} bg={bgColor} borderColor={borderColor} borderWidth="1px" borderRadius="xl">
                    <CardBody py={3}>
                        <VStack align="stretch" spacing={2}>
                            <HStack spacing={2}>
                                <Icon as={MdDraw} color="blue.500" boxSize={4} />
                                <Text fontSize="sm" fontWeight="600">
                                    Use the polygon drawing tool on the map to create a new zone
                                </Text>
                            </HStack>
                            <AnimatePresence>
                                {tempGeoJSON && (
                                    <MotionBox initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:'auto' }}
                                        exit={{ opacity:0, height:0 }} transition={{ duration:0.25 }}>
                                        <HStack p={2} bg="green.50" borderRadius="lg" borderWidth="1px" borderColor="green.200">
                                            <Icon as={MdCheckCircle} color="green.500" />
                                            <Text fontSize="sm" color="green.700" fontWeight="600">
                                                Zone drawn on map — please name it below
                                            </Text>
                                            <Button size="xs" colorScheme="red" variant="ghost" onClick={handleClearDrawing}
                                                ml="auto" borderRadius="full">Clear</Button>
                                        </HStack>
                                    </MotionBox>
                                )}
                            </AnimatePresence>
                        </VStack>
                    </CardBody>
                </Card>
            </MotionBox>

            {/* ═══════ MAP ═══════ */}
            <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(4), ...ANIM.smooth }}>
                <Card mb={6} borderWidth="1px" borderRadius="xl" overflow="hidden" height="600px"
                    boxShadow="lg" borderColor={borderColor}>
                    <MapContainer center={[25.07, -77.34]} zoom={12}
                        style={{ height:'100%', width:'100%' }} ref={mapRef}>
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' />
                        <FeatureGroup ref={(ref) => ref && setDrawnItems(ref)}>
                            <EditControl position="topright" onCreated={handleOnCreated}
                                onEdited={handleOnEdited} onDeleted={handleOnDeleted}
                                draw={{ rectangle:false, circle:false, circlemarker:false, marker:false, polyline:false,
                                    polygon:{ allowIntersection:false, showArea:true, metric:true,
                                        shapeOptions:{ color:nextFreeColor || '#3182ce', fillOpacity:0.3 } } }} // --- UPDATE: next free zone colour
                                edit={{ edit:true, remove:true }} />
                        </FeatureGroup>
                        {zones.map((zone, index) => {
                            const geoJsonData = parseGeoJSON(zone.polygonGeoJSON);
                            // --- UPDATE: the zone's own colour + constituency + live numbers in the pop-up
                            const zc = colorMap.get(zone.id) || getZoneColor(index);
                            const zi = intelById.get(String(zone.id));
                            const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c]));
                            return geoJsonData ? (
                                <GeoJSON key={`${zone.id}-${zc}-${zi ? zi.stats.open : 'x'}`} data={geoJsonData}
                                    style={{ color:zc, fillColor:zc, fillOpacity:0.25, weight:2 }}
                                    onEachFeature={(feature, layer) => {
                                        layer.bindPopup(`
                                            <div style="font-family:sans-serif;">
                                                <h3 style="margin:0 0 4px;color:#2d3748;"><span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${esc(zc)};margin-right:6px"></span>${esc(zone.name)}</h3>
                                                ${(zi?.constituency || zone.constituency) ? `<p style="margin:0 0 6px;color:#718096;font-size:12px;">Constituency: ${esc(zi?.constituency || zone.constituency)}</p>` : ''}
                                                <p style="margin:0;color:#4a5568;font-size:14px;">${esc(zone.description || 'No description')}</p>
                                                ${zi ? `<p style="margin:6px 0 0;color:#2d3748;font-size:12px;"><b>${zi.stats.open}</b> open · <b>${zi.stats.critical}</b> critical · <b>${zi.stats.techs_online}</b> staff online · risk <b>${zi.risk.score}</b></p>` : ''}
                                            </div>`);
                                    }} />
                            ) : null;
                            // --- END UPDATE
                        })}
                    </MapContainer>
                </Card>
            </MotionBox>

            {/* ═══════ ZONES TABLE ═══════ */}
            <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(5), ...ANIM.smooth }}>
                <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" borderRadius="xl" boxShadow="sm">
                    <CardBody>
                        <HStack mb={4} justify="space-between">
                            <HStack spacing={2}>
                                <Icon as={MdLayers} color="blue.400" boxSize={4} />
                                <Heading size="sm" fontWeight="700">Defined Zones</Heading>
                                <Badge colorScheme="blue" borderRadius="full" variant="subtle" fontSize="2xs">
                                    {filteredZones.length}
                                </Badge>
                            </HStack>
                            <InputGroup maxW="280px" size="sm">
                                <InputLeftElement pointerEvents="none">
                                    <SearchIcon color="gray.400" boxSize={3} />
                                </InputLeftElement>
                                <Input placeholder="Search zones…" value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    bg={searchBg} borderRadius="lg" fontSize="sm"
                                    _focus={{ borderColor:'blue.400', boxShadow:'0 0 0 1px var(--chakra-colors-blue-400)' }} />
                                {searchTerm && (
                                    <InputRightElement cursor="pointer" onClick={() => setSearchTerm('')}>
                                        <CloseIcon color="gray.400" boxSize={2} />
                                    </InputRightElement>
                                )}
                            </InputGroup>
                        </HStack>

                        <TableContainer>
                            {isLoading && <Progress size="xs" isIndeterminate colorScheme="teal" mb={2} />}
                            <Table variant="simple" size="sm">
                                <Thead>
                                    <Tr>
                                        <Th>Zone / Constituency</Th>
                                        <Th>Description</Th>
                                        <Th isNumeric>Open jobs</Th>{/* --- UPDATE */}
                                        <Th isNumeric>Staff online</Th>
                                        <Th>Risk</Th>{/* --- END UPDATE */}
                                        <Th>Created</Th>
                                        <Th>Actions</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {isLoading && zones.length === 0 && Array.from({ length:3 }).map((_, i) => (
                                        <Tr key={i}>{Array.from({ length:7 }).map((_, j) => (
                                            <Td key={j}><Skeleton h="14px" w={j===0?'130px':'100px'} borderRadius="md" /></Td>
                                        ))}</Tr>
                                    ))}
                                    {error && !isLoading && (
                                        <Tr><Td colSpan={7}>
                                            <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>
                                        </Td></Tr>
                                    )}
                                    {!isLoading && !error && filteredZones.length === 0 && (
                                        <Tr><Td colSpan={7}>
                                            <EmptyState icon={MdMap}
                                                title={searchTerm ? 'No zones match your search' : 'No zones defined yet'}
                                                subtitle="Draw a polygon on the map above to create one" />
                                        </Td></Tr>
                                    )}
                                    <AnimatePresence>
                                        {!isLoading && filteredZones.map((zone, index) => (
                                            <ZoneRow key={zone.id} zone={zone} index={index}
                                                onDelete={initiateDelete}
                                                color={colorMap.get(zone.id)} intel={intelById.get(String(zone.id))} onEdit={openEdit} /> /* --- UPDATE */
                                        ))}
                                    </AnimatePresence>
                                </Tbody>
                            </Table>
                        </TableContainer>
                    </CardBody>
                </Card>
            </MotionBox>

            {/* ═══════ CREATE ZONE MODAL ═══════ */}
            <Modal isOpen={isOpen} onClose={resetForm} size="lg" motionPreset="slideInBottom">
                <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.400" />
                <ModalContent borderRadius="2xl" overflow="hidden">
                    <Box position="absolute" top={0} left={0} right={0} h="3px"
                        bgGradient={headerGradient} />
                    <ModalHeader pt={5}>
                        <HStack spacing={2}>
                            <Icon as={MdLocationOn} color="teal.500" />
                            <Text fontWeight="800">Name Your New Zone</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton borderRadius="full" />
                    <ModalBody>
                        <VStack spacing={4}>
                            <HStack p={3} bg={infoBg} borderRadius="lg" w="full" borderWidth="1px" borderColor="blue.200">
                                <Icon as={MdCheckCircle} color="blue.500" />
                                <Text fontSize="sm">A zone has been drawn on the map. Please provide a name and description.</Text>
                            </HStack>
                            <FormControl isRequired isInvalid={!!formErrors.name}>
                                <FormLabel fontSize="sm" fontWeight="600">Zone Name</FormLabel>
                                <Input value={zoneName} onChange={e => { setZoneName(e.target.value); setFormErrors(prev => ({ ...prev, name:null })); }}
                                    placeholder="e.g., Northern District" maxLength={255} borderRadius="lg"
                                    _focus={{ borderColor:'teal.400', boxShadow:'0 0 0 1px var(--chakra-colors-teal-400)' }} />
                                {formErrors.name && <Text color="red.500" fontSize="xs" mt={1}>{formErrors.name}</Text>}
                                <Text fontSize="2xs" color={mutedColor} mt={1}>{zoneName.length}/255</Text>
                            </FormControl>
                            <FormControl isInvalid={!!formErrors.description}>
                                <FormLabel fontSize="sm" fontWeight="600">Description (Optional)</FormLabel>
                                <Textarea value={zoneDescription}
                                    onChange={e => { setZoneDescription(e.target.value); setFormErrors(prev => ({ ...prev, description:null })); }}
                                    placeholder="Describe the coverage area…" rows={3} maxLength={500} borderRadius="lg" />
                                {formErrors.description && <Text color="red.500" fontSize="xs" mt={1}>{formErrors.description}</Text>}
                                <Text fontSize="2xs" color={mutedColor} mt={1}>{zoneDescription.length}/500</Text>
                            </FormControl>
                            {/* --- UPDATE: constituency + colour */}
                            <FormControl>
                                <FormLabel fontSize="sm" fontWeight="600">Constituency (Optional)</FormLabel>
                                <Input value={zoneConstituency} onChange={e => setZoneConstituency(e.target.value)} maxLength={120}
                                    placeholder="e.g., Bain & Grants Town — a zone and a constituency are the same thing" borderRadius="lg" />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm" fontWeight="600"><Icon as={MdPalette} mr={1} />Zone Colour</FormLabel>
                                <ColorPicker value={zoneColor} onChange={setZoneColor} usedColors={[...colorMap.values()]} />
                                <Text fontSize="2xs" color={mutedColor} mt={1}>Leave on automatic to get the next unused colour ({nextFreeColor}).</Text>
                            </FormControl>
                            {/* --- END UPDATE */}
                            <FormControl>
                                <FormLabel fontSize="sm" fontWeight="600">GeoJSON Data (Auto-generated)</FormLabel>
                                <Textarea value={tempGeoJSON || ''} isDisabled height="80px"
                                    fontFamily="mono" fontSize="xs" borderRadius="lg" />
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={resetForm} size="sm" borderRadius="lg">Cancel</Button>
                        <Button colorScheme="teal" onClick={handleSaveZone} isLoading={isLoading}
                            leftIcon={<CheckIcon />} size="sm" borderRadius="lg"
                            _hover={{ transform:'translateY(-1px)', boxShadow:'lg' }} transition="all 0.2s">
                            Save Zone
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* --- UPDATE: EDIT ZONE MODAL --- */}
            <Modal isOpen={isEditOpen} onClose={onEditClose} size="lg" motionPreset="slideInBottom">
                <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.400" />
                <ModalContent borderRadius="2xl" overflow="hidden">
                    <Box position="absolute" top={0} left={0} right={0} h="4px" bg={isHexColor(editForm.color) ? editForm.color : (editing ? colorMap.get(editing.id) : 'teal.400')} />
                    <ModalHeader pt={5}><HStack spacing={2}><Icon as={MdEdit} color="teal.500" /><Text fontWeight="800">Edit zone / constituency</Text></HStack></ModalHeader>
                    <ModalCloseButton borderRadius="full" />
                    <ModalBody>
                        <VStack spacing={4}>
                            <FormControl isRequired isInvalid={!!editErrors.name}>
                                <FormLabel fontSize="sm" fontWeight="600">Zone Name</FormLabel>
                                <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name:e.target.value }))} maxLength={255} borderRadius="lg" aria-label="Edit zone name" />
                                {editErrors.name && <Text color="red.500" fontSize="xs" mt={1}>{editErrors.name}</Text>}
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm" fontWeight="600">Constituency</FormLabel>
                                <Input value={editForm.constituency} onChange={e => setEditForm(f => ({ ...f, constituency:e.target.value }))} maxLength={120} borderRadius="lg" aria-label="Edit constituency" placeholder="Constituency name (optional)" />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm" fontWeight="600">Description</FormLabel>
                                <Textarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description:e.target.value }))} rows={2} maxLength={500} borderRadius="lg" />
                            </FormControl>
                            <FormControl isInvalid={!!editErrors.color}>
                                <FormLabel fontSize="sm" fontWeight="600"><Icon as={MdPalette} mr={1} />Colour</FormLabel>
                                <ColorPicker value={editForm.color} onChange={(c) => setEditForm(f => ({ ...f, color:c }))} usedColors={[...colorMap.entries()].filter(([id]) => id !== editing?.id).map(([, c]) => c)} />
                                {editErrors.color && <Text color="red.500" fontSize="xs" mt={1}>{editErrors.color}</Text>}
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onEditClose} size="sm" borderRadius="lg">Cancel</Button>
                        <Button colorScheme="teal" onClick={saveEdit} isLoading={isSavingEdit} leftIcon={<CheckIcon />} size="sm" borderRadius="lg">Save</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            {/* --- END UPDATE --- */}

            {/* ═══════ DELETE DIALOG ═══════ */}
            <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose}
                motionPreset="slideInBottom">
                <AlertDialogOverlay backdropFilter="blur(4px)">
                    <AlertDialogContent borderRadius="xl">
                        <AlertDialogHeader fontSize="md" fontWeight="700">
                            <HStack spacing={2}><Icon as={MdWarning} color="red.500" /><Text>Delete Zone</Text></HStack>
                        </AlertDialogHeader>
                        <AlertDialogBody fontSize="sm">
                            Are you sure you want to delete "<Text as="span" fontWeight="700">{zoneToDelete?.name}</Text>"?
                            <Box mt={3} p={3} bg="red.50" borderRadius="lg" borderWidth="1px" borderColor="red.200">
                                <Text fontSize="xs" color="red.700"><Text as="span" fontWeight="700">Warning:</Text> Assignment rules associated with this zone may be affected.</Text>
                            </Box>
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onDeleteClose} size="sm" borderRadius="lg">Cancel</Button>
                            <Button colorScheme="red" onClick={handleDeleteZone} ml={3} isLoading={isLoading}
                                size="sm" borderRadius="lg" leftIcon={<DeleteIcon />}>Delete Zone</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default ZoneManagementComponent;